import * as React from 'react';
import { Info } from 'lucide-react';

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';

export interface TitleDescriptionInfoProps {
  description: React.ReactNode;
  infoAriaLabel?: string;
  /** Raise popover above drawers (uses z-overlay) */
  elevated?: boolean;
}

export const TitleDescriptionInfo: React.FC<TitleDescriptionInfoProps> = ({
  description,
  infoAriaLabel,
  elevated = false,
}) => {
  const isMobile = useIsMobile();
  const [open, setOpen] = React.useState(false);
  const ariaLabel =
    infoAriaLabel ??
    (typeof description === 'string' ? description : 'More information');

  const triggerButton = (
    <button
      type="button"
      className="focus-brand-shadow inline-flex shrink-0 rounded-full text-muted-foreground/50 transition-colors hover:text-muted-foreground"
      aria-label={ariaLabel}
    >
      <Info className="h-3 w-3" strokeWidth={2} aria-hidden />
    </button>
  );

  const pointerHoveringRef = React.useRef(false);

  if (isMobile) {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>{triggerButton}</PopoverTrigger>
        <PopoverContent
          side="top"
          align="start"
          className={cn(
            'max-w-[min(300px,calc(100vw-2rem))] p-3',
            elevated && 'z-overlay'
          )}
        >
          <div className="text-sm leading-relaxed">{description}</div>
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip
        open={open}
        onOpenChange={next => {
          if (next && !pointerHoveringRef.current) return;
          setOpen(next);
        }}
      >
        <TooltipTrigger asChild>
          <button
            type="button"
            className="focus-brand-shadow inline-flex shrink-0 rounded-full text-muted-foreground/50 transition-colors hover:text-muted-foreground"
            aria-label={ariaLabel}
            onPointerEnter={() => {
              pointerHoveringRef.current = true;
              setOpen(true);
            }}
            onPointerLeave={() => {
              pointerHoveringRef.current = false;
              setOpen(false);
            }}
            onBlur={() => {
              if (!pointerHoveringRef.current) {
                setOpen(false);
              }
            }}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setOpen(previous => !previous);
              }
              if (e.key === 'Escape') {
                setOpen(false);
              }
            }}
          >
            <Info className="h-3 w-3" strokeWidth={2} aria-hidden />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" align="start" className="max-w-[300px] p-3">
          <div className="text-sm leading-relaxed">{description}</div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
